import type { FocusEvent, FormEvent } from 'react';
import { forwardRef, useCallback } from 'react';

import {
  Box,
  LoadingSpinner,
  Textfield,
  useTheme,
} from '@simplywallst/ui-core';

import ResetSolid from '@simplywallst/ui-core/icons/ResetSolid';
import SearchSolid from '@simplywallst/ui-core/icons/SearchSolid';

import { useKeys } from '@/hooks/useKeys';

import { useSearch } from '@/hooks/useSearch';

const keys = ['ArrowUp', 'ArrowDown', 'Enter'] as const;

interface InputProps {
  value: string;
  status: ReturnType<typeof useSearch>['status'];
  name?: string;
  placeholder?: string;
  onChange: (value: string) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onReset?: () => void;
  onArrowDown?: () => void;
  onArrowUp?: () => void;
  onEnter?: () => void;
}

export const SearchInput = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      value,
      status,
      name = 'search-field',
      placeholder = 'Type ticker or company',
      onFocus,
      onChange,
      onReset,
      onArrowDown,
      onArrowUp,
      onEnter,
    },
    ref
  ) => {
    const theme = useTheme();

    const innerRef = typeof ref === 'function' ? null : ref?.current;

    const handleChange = useCallback(
      (e: FormEvent<HTMLInputElement>) => {
        onChange(e.currentTarget.value);
      },
      [onChange]
    );

    const handleReset = useCallback(() => {
      onChange('');
      onReset?.();
    }, [onChange, onReset, innerRef]);

    useKeys({
      targetKeys: keys,
      targetRef: innerRef,
      preventDefaut: true,
      callback(keyPress) {
        switch (keyPress) {
          case 'ArrowDown':
            onArrowDown?.();
            break;
          case 'ArrowUp':
            onArrowUp?.();
            break;
          case 'Enter':
            onEnter?.();
            break;
        }
        return;
      },
    });

    return (
      <Textfield
        ref={ref}
        name={name}
        type="search"
        value={value}
        placeholder={placeholder}
        onFocus={onFocus}
        onChange={handleChange}
        autoComplete="off"
        prefix={<SearchSolid fill={theme.color.brand01} />}
        postfix={
          value !== '' && status === 'pending' ? (
            <Box width="16px">
              <LoadingSpinner styleType="light" />
            </Box>
          ) : value.length > 0 ? (
            <ResetSolid
              tabIndex={0}
              onClick={handleReset}
              fill={theme.color.white}
              cursor="pointer"
              opacity={0.5}
              data-cy-id="search-field-reset"
            />
          ) : null
        }
      />
    );
  }
);

SearchInput.displayName = 'SearchInput';
